import { __ } from '@wordpress/i18n';
import { check } from '@wordpress/icons';
import { useState, useEffect } from '@wordpress/element';
import { store as noticesStore } from '@wordpress/notices';
import { Button, TextareaControl, Icon } from '@wordpress/components';
import { useSelect, useDispatch } from '@wordpress/data';
import apiFetch from '@wordpress/api-fetch';

import { selectProps } from '../utils/types';
import { editorStore } from '../utils/store';
import { isAnimationEnabled, showAnimatedAiText } from '../utils';

/**
 * Summary.
 *
 * This Component returns the Summary
 * label and button.
 *
 * @since 1.1.0
 *
 * @return {JSX.Element} Summary.
 */
const Summary = (): JSX.Element => {
	const [ summary, setSummary ] = useState( '' );
	const { editPost, savePost } = useDispatch( editorStore ) as any;
	const { createNotice, createErrorNotice, removeNotice } =
		useDispatch( noticesStore );
	const { postId, postContent, postSummary, notices } = useSelect(
		( select ) => {
			const { getNotices } = select( noticesStore );
			const {
				getCurrentPostId,
				getEditedPostContent,
				getEditedPostAttribute,
			} = select( editorStore ) as selectProps;

			return {
				postId: getCurrentPostId(),
				postContent: getEditedPostContent(),
				postSummary: getEditedPostAttribute( 'meta' )?.apbe_summary,
				notices: getNotices(),
			};
		},
		[]
	);

	useEffect( () => {
		setSummary( postSummary );
	}, [ postSummary ] );

	/**
	 * This function fires when the user clicks
	 * the `Generate` button.
	 *
	 * @since 1.1.0
	 *
	 * @return { void }
	 */
	const handleClick = async (): Promise< void > => {
		notices.forEach( ( notice ) => removeNotice( notice.id ) );
		createNotice(
			'info',
			__(
				'AI is generating text, please hold on for a bit.',
				'ai-plus-block-editor'
			),
			{
				isDismissible: true,
				id: 'apbe-info',
				type: 'snackbar',
			}
		);

		try {
			const aiSummary: string = await apiFetch( {
				path: '/ai-plus-block-editor/v1/sidebar',
				method: 'POST',
				data: {
					id: postId,
					text: postContent?.text || postContent,
					feature: 'summary',
				},
			} );

			if ( isAnimationEnabled() ) {
				await showAnimatedAiText( aiSummary, setSummary );
			} else {
				setSummary( aiSummary );
			}
			editPost( { excerpt: aiSummary } );
			editPost( { meta: { apbe_summary: aiSummary } } );
			removeNotice( 'apbe-info' );
		} catch ( e ) {
			removeNotice( 'apbe-info' );
			createErrorNotice(
				__(
					'Error! Failed to fetch Summary. Please check your error logs or console for more info.',
					'ai-plus-block-editor'
				)
			);
			// eslint-disable-next-line
			console.error( e.message );
		}
	};

	/**
	 * This function fires when the user selects
	 * the AI generated result.
	 *
	 * @since 1.1.0
	 *
	 * @return { void }
	 */
	const handleSelection = (): void => {
		editPost( { excerpt: summary } );
		editPost( { meta: { apbe_summary: summary } } );
		savePost();
	};

	return (
		<>
			<p>
				<strong>{ __( 'Summary', 'ai-plus-block-editor' ) }</strong>
			</p>
			<TextareaControl
				data-testid="summary"
				rows={ 4 }
				value={ summary }
				onChange={ ( text ) => setSummary( text ) }
				__nextHasNoMarginBottom
			/>
			<div className="apbe-button-group">
				<Button
					variant="primary"
					onClick={ handleClick }
					data-testid="summary-btn"
				>
					{ __( 'Generate', 'ai-plus-block-editor' ) }
				</Button>
				<Button
					variant="secondary"
					onClick={ handleSelection }
					data-testid="summary-check"
				>
					<Icon icon={ check } />
				</Button>
			</div>
		</>
	);
};

export default Summary;
